import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentParagraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <article className="pt-24 pb-16 md:pt-28 md:pb-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                {category && (
                  <>
                    <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors">
                      {category.name}
                    </Link>
                    <i className="ri-arrow-right-s-line"></i>
                  </>
                )}
                <span className="text-foreground-700 line-clamp-1">{article.title}</span>
              </nav>

              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium">
                    {category?.name}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500">{article.readTime}</span>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 text-sm text-foreground-600">
                  <span>作者：{article.author}</span>
                </div>
              </div>

              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="prose-custom">
                {contentParagraphs.map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={index} className="text-xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith('### ')) {
                    return (
                      <h3 key={index} className="text-lg font-semibold text-foreground-800 mt-8 mb-3">
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (paragraph.startsWith('- ')) {
                    return (
                      <ul key={index} className="list-disc pl-6 my-3 space-y-1.5">
                        {paragraph.split('\n').filter(Boolean).map((item, i) => (
                          <li key={i} className="text-sm text-foreground-700 leading-relaxed">
                            {item.replace(/^- /, '')}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="text-sm text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 mt-10 pt-8 border-t border-background-200 flex-wrap">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-14 md:py-16 bg-background-100/60">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Person', name: article.author },
            image: article.coverImage,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiye.com' },
                ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://aiye.com/category/${category.slug}` }] : []),
                { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
              ],
            },
          }),
        }}
      />
      <Footer />
    </>
  );
}